import React, { useEffect, useRef, useState } from "react";
import { BaseButton, BaseCheckbox, BaseInput } from "src/components";
import { useModal } from "src/recoil/modalState/hook";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import produce from "immer";
import moment from "moment";
import { Row } from "react-table";
import { addClosingRecurrenceAsync } from "src/api/product/product-api";
import CustomTimePicker from "src/components/CustomTimePicker";
import RangeDatepicker from "src/components/RangeDatepicker";
import { RESERVATION_POLICY_QUERY_KEY } from "src/hooks/product/useGetReservationPolicy";
import { dayOptions, UnionDayOfWeekType } from "src/pages/workOrder/workOrder-types";
import { useToast } from "src/recoil/toast/hook";
import { axiosInstance } from "src/utils";
import ClosedPeriodTable from "../components/ClosedPeriodTable";
import {
  CLOSING_TITLE_INVALID_MESSAGE,
  CLOSING_TITLE_PLACEHOLDER,
  countClosingRecurrences,
  isClosingTitleInvalid,
  toClosingRecurrence,
} from "../utils/closingTime";
import { DAYS, toClosingTimePolicyOwner } from "./ClosedPeriod";
import { ReservationProps } from "./ReservationAvailabilityPeriod";

type UnionDay = "SUN" | "MON" | "TUE" | "WED" | "THU" | "FRI" | "SAT";
export interface ClosedPeriodListForm {
  startDate?: Date | null;
  endDate?: Date | null;
  days?: Array<UnionDay>;
  title?: string; // 휴무 사유. 휴무일 한 줄에 하나이며 시간이 여러 개면 모두 같은 사유로 저장된다
  times?: {
    startTime?: string;
    endTime?: string;
  }[];
}

export type ValidationType = {
  feild?: string;
  message?: string;
  isPass?: boolean;
};

/**
 * 순차 저장 중 앞에서부터 savedCount 건이 저장됐을 때 저장된 항목을 폼에서 덜어낸다.
 * 저장 순서는 "줄 순서 × 줄 안의 시간 순서"라 같은 순서로 세어 나간다.
 */
const dropSavedClosedPeriods = (list: ClosedPeriodListForm[], savedCount: number): ClosedPeriodListForm[] => {
  let cursor = 0;

  return list
    .map((row) => ({ ...row, times: (row.times ?? []).filter(() => cursor++ >= savedCount) }))
    .filter((row) => (row.times?.length ?? 0) > 0);
};

const ClosedPeriodRegisterColumns = [
  {
    Header: "휴무 기간",
    accessor: "startDate",
    width: 240,
    Cell: ({
      row,
      setClosedPeriodList,
    }: {
      row: Row<ClosedPeriodListForm>;
      setClosedPeriodList: React.Dispatch<React.SetStateAction<ClosedPeriodListForm[]>>;
    }) => {
      const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);

      const handleOnDateRangeChange = (dateRange: [Date | null, Date | null]) => {
        const [startDate, endDate] = dateRange;

        setDateRange(dateRange);

        setClosedPeriodList(
          produce((draftArr) => {
            draftArr.forEach((draft, index) => {
              if (index === row.index) {
                draft.startDate = startDate;
                draft.endDate = endDate;
              }
            });
          }),
        );
      };

      return (
        <div className="minmax220">
          <RangeDatepicker minDate={new Date()} dateRange={dateRange} onChange={handleOnDateRangeChange} />
        </div>
      );
    },
  },
  {
    Header: "휴무 요일",
    accessor: "days",
    width: 320,
    Cell: ({
      row,
      setClosedPeriodList,
    }: {
      row: Row<ClosedPeriodListForm>;
      setClosedPeriodList: React.Dispatch<React.SetStateAction<ClosedPeriodListForm[]>>;
    }) => {
      const days = row?.original?.days;
      return (
        <div>
          <div className="flex-center-center h40">
            <BaseCheckbox
              label="전체"
              id={`allCheck-${row.index}`}
              name={`allCheck-${row.index}`}
              className="mr10"
              checked={days?.length === 7}
              onChange={(checked) => {
                if (checked) {
                  // 모든 요일 넣기
                  setClosedPeriodList(
                    produce((draftArr) => {
                      draftArr.forEach((draft, index) => {
                        if (index === row.index) {
                          draft.days = DAYS as any;
                        }
                      });
                    }),
                  );
                } else {
                  // 빈 배열 넣기
                  setClosedPeriodList(
                    produce((draftArr) => {
                      draftArr.forEach((draft, index) => {
                        if (index === row.index) {
                          draft.days = [];
                        }
                      });
                    }),
                  );
                }
              }}
            />
            {dayOptions.map((day, idx: number) => (
              <div key={day.value}>
                <BaseCheckbox
                  id={`${day.value}-${row.index}`}
                  name={`${day.value}-${row.index}`}
                  className="mr4 chip-case-product"
                  label={day.label}
                  checked={row?.original?.days?.some((item: UnionDayOfWeekType) => item === day.value) || false}
                  onChange={(checked: boolean) => {
                    let weeks: UnionDayOfWeekType[] = row?.original?.days ? Array.from(row?.original?.days) : [];
                    if (checked) {
                      weeks.push(day.value);
                    } else {
                      weeks = weeks.filter((week) => week !== day.value);
                    }

                    setClosedPeriodList(
                      produce((draftArr) => {
                        draftArr.forEach((draft, index) => {
                          if (index === row.index) {
                            draft.days = weeks;
                          }
                        });
                      }),
                    );
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      );
    },
  },
  {
    Header: "휴무 시간",
    accessor: "times",
    width: 320,
    Cell: ({
      row,
      setClosedPeriodList,
    }: {
      row: Row<ClosedPeriodListForm>;
      setClosedPeriodList: React.Dispatch<React.SetStateAction<ClosedPeriodListForm[]>>;
    }) => {
      const times = row?.original?.times ?? [{ startTime: "", endTime: "" }];

      return times?.map((timeData, timeIndexX) => {
        return (
          <div key={timeIndexX} className="flex-center-center">
            <CustomTimePicker
              name="startTime"
              className="w120 position-relative"
              timeIntervals={30}
              setDate={(date) => {
                // 휴무시간 > endTime 보다 startTime을 이후 시간으로 했을 때 endTime "" 처리
                const start = moment(date, "HH:mm").format("YYYY-MM-DD HH:mm");
                const end = moment(timeData.endTime, "HH:mm").format("YYYY-MM-DD HH:mm");
                const isStartTimeAfterEnd = moment(start).isAfter(end);

                setClosedPeriodList(
                  produce((draftArr) => {
                    draftArr.forEach((draft, index) => {
                      if (index === row.index) {
                        draft?.times?.forEach((time, timeIndexY) => {
                          if (timeIndexY === timeIndexX) {
                            time.startTime = date;
                            if (isStartTimeAfterEnd) time.endTime = "";
                          }
                        });
                      }
                    });
                  }),
                );
              }}
              selectedTime={timeData.startTime}
              midnight
              portalToBody
              placeholder=" "
            />
            <span className="mx10">-</span>
            <CustomTimePicker
              name="endTime"
              className="w120"
              timeIntervals={30}
              setDate={(date) => {
                setClosedPeriodList(
                  produce((draftArr) => {
                    draftArr.forEach((draft, index) => {
                      if (index === row.index) {
                        draft?.times?.forEach((time, timeIndexY) => {
                          if (timeIndexY === timeIndexX) {
                            time.endTime = date;
                          }
                        });
                      }
                    });
                  }),
                );
              }}
              minTime={timeData.startTime}
              selectedTime={timeData.endTime}
              midnight
              portalToBody
              placeholder=" "
            />
            <div className="ml10">
              {timeIndexX === 0 ? (
                <div className="flex-center-center">
                  <div
                    className="plus-btn py10"
                    onClick={() => {
                      setClosedPeriodList(
                        produce((draftArr) => {
                          draftArr.forEach((draft, index) => {
                            if (index === row.index) {
                              draft?.times?.push({
                                startTime: "00:00",
                                endTime: "24:00",
                              });
                            }
                          });
                        }),
                      );
                    }}
                  ></div>
                </div>
              ) : (
                <div className="flex-center-center">
                  <button
                    className="minus-btn"
                    onClick={() => {
                      setClosedPeriodList(
                        produce((draftArr) => {
                          draftArr[row.index]?.times?.splice(timeIndexX, 1);
                        }),
                      );
                    }}
                    type="button"
                  />
                </div>
              )}
            </div>
          </div>
        );
      });
    },
  },
  {
    Header: "휴무 사유",
    accessor: "title",
    width: 300,
    Cell: ({
      row,
      setClosedPeriodList,
    }: {
      row: Row<ClosedPeriodListForm>;
      setClosedPeriodList: React.Dispatch<React.SetStateAction<ClosedPeriodListForm[]>>;
    }) => {
      const title = row?.original?.title ?? "";

      return (
        <BaseInput
          name={`title-${row.index}`}
          value={title}
          placeholder={CLOSING_TITLE_PLACEHOLDER}
          errorText={isClosingTitleInvalid(title) ? CLOSING_TITLE_INVALID_MESSAGE : undefined}
          onChange={(value: string) => {
            setClosedPeriodList(
              produce((draftArr) => {
                draftArr.forEach((draft, index) => {
                  if (index === row.index) {
                    draft.title = value;
                  }
                });
              }),
            );
          }}
        />
      );
    },
  },
  {
    Header: "삭제",
    accessor: "delete",
    width: 50,
    Cell: ({
      row,
      setClosedPeriodList,
    }: {
      row: Row<ClosedPeriodListForm>;
      setClosedPeriodList: React.Dispatch<React.SetStateAction<ClosedPeriodListForm[]>>;
    }) => {
      return (
        <button
          className="base-trash-btn color-bg-gray h40"
          onClick={() => {
            setClosedPeriodList(
              produce((draftArr) => {
                draftArr.splice(row.index, 1);
              }),
            );
          }}
        ></button>
      );
    },
  },
];

const ClosedPeriodRegister = ({ spaceType, space, serviceType, serviceId, isDefault }: ReservationProps) => {
  const [validations, setValidations] = useState<ValidationType[]>([]);

  const { setBaseModal, setAbstractModalZ2 } = useModal();
  const queryClient = useQueryClient();

  const defaultValue = {
    startDate: null,
    endDate: null,
    days: ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"] as Array<UnionDay>,
    title: "",
    times: [
      {
        startTime: "00:00",
        endTime: "24:00",
      },
    ],
  };

  const [closedPeriodList, setClosedPeriodList] = useState<ClosedPeriodListForm[]>([defaultValue]);

  const owner = toClosingTimePolicyOwner(serviceType, serviceId);
  const target = isDefault ? { facilityType: spaceType } : { facilityId: space?.id };

  // 중간에 실패했을 때 앞서 저장된 건수를 알아야 해서 따로 센다
  const savedCountRef = useRef(0);

  // 휴무 추가는 항목 1건당 1요청이고 서버가 조회-병합-저장으로 처리한다.
  // 병렬로 보내면 서로의 결과를 덮어쓰므로 반드시 순차로 보낸다.
  const { mutate, isPending } = useMutation({
    mutationFn: async (list: ClosedPeriodListForm[]) => {
      savedCountRef.current = 0;

      for (const closedPeriod of list) {
        for (const time of closedPeriod.times ?? []) {
          await addClosingRecurrenceAsync(axiosInstance, {
            ...owner,
            ...target,
            ...toClosingRecurrence({
              startDate: closedPeriod.startDate,
              endDate: closedPeriod.endDate,
              days: closedPeriod.days,
              title: closedPeriod.title,
              startTime: time.startTime,
              endTime: time.endTime,
            }),
          });

          savedCountRef.current += 1;
        }
      }
    },
  });
  const { openToast } = useToast();

  // 필수값 데이터 적용시 validation 통과 적용
  useEffect(() => {
    if (validations.length > 0) {
      const dateRequired = closedPeriodList.some((item) => !item.startDate || !item.endDate);
      const daysRequired = closedPeriodList.some((item) => item.days?.length === 0);
      const timeRequired = closedPeriodList.some((item) => item.times?.some((time) => !time.startTime || !time.endTime));
      const titleInvalid = closedPeriodList.some((item) => isClosingTitleInvalid(item.title));

      !dateRequired && setValidations((prev) => prev.filter((item) => item.feild !== "date"));
      !daysRequired && setValidations((prev) => prev.filter((item) => item.feild !== "days"));
      !timeRequired && setValidations((prev) => prev.filter((item) => item.feild !== "time"));
      !titleInvalid && setValidations((prev) => prev.filter((item) => item.feild !== "title"));
    }
  }, [closedPeriodList]);

  // 등록 버튼 클릭시 > 유효성 검사 확인
  const handleValidation = (): boolean => {
    let pass = true;

    closedPeriodList.forEach((item) => {
      if (!item.startDate || !item.endDate) {
        pass = false;
        setValidations((prev) => [...prev, { message: "휴무 기간을 선택해주세요.", feild: "date" }]);
      }

      if (item.days && item.days.length === 0) {
        pass = false;
        setValidations((prev) => [...prev, { message: "휴무 요일을 선택해주세요.", feild: "days" }]);
      }

      if (item.times?.some((time) => !time.startTime || !time.endTime)) {
        pass = false;
        setValidations((prev) => [...prev, { message: "휴무 시간을 선택해주세요.", feild: "time" }]);
      }

      // 휴무 사유는 선택값이고, 글자 수 에러는 해당 인풋 아래에 인라인으로 노출된다
      if (isClosingTitleInvalid(item.title)) {
        pass = false;
        setValidations((prev) => [...prev, { message: CLOSING_TITLE_INVALID_MESSAGE, feild: "title" }]);
      }
    });

    if (pass) onCreateClosedData();
    return false;
  };

  const onCreateClosedData = () => {
    // 개별 설정은 공용공간 ID 가 있어야 어느 정책에 넣을지 정해진다
    if (!isDefault && !space?.id) {
      openToast({ content: "공용공간 정보를 찾을 수 없습니다.", type: "FAIL" });
      return;
    }

    setBaseModal({
      isOpen: true,
      title: "저장하시겠습니까?",
      children: "저장 후에는 변경 사항이 즉시 적용됩니다.",
      btnLeftTitle: "취소",
      btnRightTitle: "확인",
      onClick: () => {
        const totalCount = countClosingRecurrences(closedPeriodList);

        mutate(closedPeriodList, {
          onSuccess: () => {
            openToast({ content: "저장 되었습니다.", isSystemToast: true });
            setAbstractModalZ2({ isOpen: false });
          },
          onError: () => {
            // 항목마다 따로 저장되므로 중간에 실패해도 앞서 보낸 건은 서버에 남는다.
            // 이미 저장된 줄을 폼에서 덜어내 같은 건을 다시 등록(중복 생성)하지 않도록 한다.
            const savedCount = savedCountRef.current;
            if (savedCount === 0) return;

            setClosedPeriodList((prev) => dropSavedClosedPeriods(prev, savedCount));
            openToast({ content: `${totalCount}건 중 ${savedCount}건이 저장되었습니다. 남은 항목만 다시 등록해 주세요.` });
          },
          onSettled: () => {
            // 실패해도 이미 저장된 건이 목록에 보여야 한다
            queryClient.refetchQueries({ queryKey: [RESERVATION_POLICY_QUERY_KEY] });
            setBaseModal({ isOpen: false });
          },
        });
      },
    });
  };

  return (
    <>
      <section className="base-abstract-modal__title">
        <h1>휴무일 등록</h1>
      </section>
      <section className="base-abstract-modal__contents-minmax-height px30">
        <ClosedPeriodTable
          setClosedPeriodList={setClosedPeriodList}
          columns={ClosedPeriodRegisterColumns}
          data={closedPeriodList ?? []}
          validations={validations}
          setValidations={setValidations}
        ></ClosedPeriodTable>
        <section className="d-flex flex-center-center pt20" style={{ position: "sticky", bottom: 0, zIndex: 99, background: "#ffffff" }}>
          <BaseButton
            className="color-white w278"
            title="새 휴무일 추가"
            onClick={() => {
              setClosedPeriodList((prev) => [...prev, defaultValue]);
            }}
          ></BaseButton>
        </section>
      </section>

      <section className="base-abstract-modal__btn-wrap">
        {closedPeriodList.length > 0 && (
          <div className="flex-center">
            {/* 휴무 기간 메세지 */}
            {validations?.some((item) => item.feild === "date") && (
              <p className="validation-text">{validations?.find((item) => item.feild === "date")?.message}</p>
            )}
            {/* 휴무 요일 메세지 */}
            {!validations?.some((item) => item.feild === "date") && validations?.some((item) => item.feild === "days") && (
              <p className="validation-text">{validations?.find((item) => item.feild === "days")?.message}</p>
            )}
            {/* 휴무 시간 메세지 */}
            {!validations?.some((item) => item.feild === "date") &&
              !validations?.some((item) => item.feild === "days") &&
              validations?.some((item) => item.feild === "time") && (
                <p className="validation-text">{validations?.find((item) => item.feild === "time")?.message}</p>
              )}
          </div>
        )}

        <BaseButton title={"취소"} className="color-white" onClick={() => setAbstractModalZ2({ isOpen: false })} />
        <BaseButton
          title={"등록"}
          className=""
          disabled={closedPeriodList.length === 0 || isPending}
          onClick={() => {
            handleValidation();
          }}
        />
      </section>
    </>
  );
};

export default ClosedPeriodRegister;
